import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, Clock, TrendingDown, TrendingUp, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PerformanceAlert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  metric: string;
  value: number;
  threshold: number;
  timestamp: Date;
  resolved: boolean;
}

const PerformanceAlerts = () => {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState<PerformanceAlert[]>([]);
  const [loading, setLoading] = useState(false);

  // Simulate real-time performance monitoring
  useEffect(() => {
    const generateAlerts = () => {
      const mockAlerts: PerformanceAlert[] = [
        {
          id: '1',
          type: 'warning',
          title: 'Page Load Time Elevated',
          message: 'Average page load time has increased above normal threshold',
          metric: 'page_load_time',
          value: 3200,
          threshold: 3000,
          timestamp: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
          resolved: false
        },
        {
          id: '2',
          type: 'critical',
          title: 'Memory Usage Critical',
          message: 'JavaScript heap usage is approaching maximum capacity',
          metric: 'memory_usage',
          value: 95,
          threshold: 90,
          timestamp: new Date(Date.now() - 2 * 60 * 1000), // 2 minutes ago
          resolved: false
        },
        {
          id: '3',
          type: 'info',
          title: 'Bundle Size Optimized',
          message: 'Application bundle size has been reduced by 15%',
          metric: 'bundle_size',
          value: 2100000,
          threshold: 2500000,
          timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
          resolved: true
        }
      ];

      setAlerts(mockAlerts);
    };

    generateAlerts();

    // Simulate real-time updates
    const interval = setInterval(() => {
      // Randomly update alert status
      setAlerts(prev => prev.map(alert => ({
        ...alert,
        resolved: Math.random() > 0.8 ? !alert.resolved : alert.resolved
      })));
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const refreshAlerts = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Alerts Refreshed",
        description: "Performance alerts have been updated.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh alerts.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, resolved: true } : alert
    ));
    
    toast({
      title: "Alert Resolved",
      description: "Performance alert has been marked as resolved.",
    });
  };

  const getAlertIcon = (type: string, resolved: boolean) => {
    if (resolved) return <CheckCircle className="w-4 h-4 text-green-600" />;
    
    switch (type) {
      case 'critical': return <AlertTriangle className="w-4 h-4 text-destructive" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default: return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  const getAlertBadgeVariant = (type: string, resolved: boolean) => {
    if (resolved) return 'secondary';
    
    switch (type) {
      case 'critical': return 'destructive' as const;
      case 'warning': return 'default' as const;
      default: return 'default' as const;
    }
  };

  const formatValue = (metric: string, value: number) => {
    switch (metric) {
      case 'page_load_time':
        return `${value}ms`;
      case 'memory_usage':
        return `${value}%`;
      case 'bundle_size':
        return `${(value / 1000000).toFixed(1)}MB`;
      default:
        return value.toString();
    }
  };

  const activeAlerts = alerts.filter(alert => !alert.resolved);
  const resolvedAlerts = alerts.filter(alert => alert.resolved);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Performance Alerts
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshAlerts}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-destructive">{activeAlerts.length}</div>
              <div className="text-sm text-muted-foreground">Active Alerts</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-green-600">{resolvedAlerts.length}</div>
              <div className="text-sm text-muted-foreground">Resolved</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{alerts.length}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
          </div>

          {/* Active Alerts */}
          {activeAlerts.length > 0 && (
            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-lg">Active Alerts</h3>
              {activeAlerts.map(alert => (
                <Alert key={alert.id} variant={alert.type === 'critical' ? 'destructive' : 'default'}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getAlertIcon(alert.type, alert.resolved)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{alert.title}</span>
                          <Badge variant={getAlertBadgeVariant(alert.type, alert.resolved)}>
                            {alert.type.toUpperCase()}
                          </Badge>
                        </div>
                        <AlertDescription className="mb-2">
                          {alert.message}
                        </AlertDescription>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>Value: {formatValue(alert.metric, alert.value)}</span>
                          <span>Threshold: {formatValue(alert.metric, alert.threshold)}</span>
                          <span>
                            {alert.value > alert.threshold ? (
                              <TrendingUp className="w-3 h-3 inline text-red-500" />
                            ) : (
                              <TrendingDown className="w-3 h-3 inline text-green-500" />
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-muted-foreground">
                        {alert.timestamp.toLocaleTimeString()}
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => resolveAlert(alert.id)}
                      >
                        Resolve
                      </Button>
                    </div>
                  </div>
                </Alert>
              ))}
            </div>
          )}

          {/* Recent Resolved Alerts */}
          {resolvedAlerts.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Recently Resolved</h3>
              {resolvedAlerts.slice(0, 3).map(alert => (
                <div key={alert.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  {getAlertIcon(alert.type, alert.resolved)}
                  <div className="flex-1">
                    <div className="font-medium text-green-800">{alert.title}</div>
                    <div className="text-sm text-green-600">{alert.message}</div>
                  </div>
                  <Badge variant="secondary">Resolved</Badge>
                </div>
              ))}
            </div>
          )}

          {alerts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
              <p>No performance alerts at this time</p>
              <p className="text-sm">Your application is performing optimally</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceAlerts;